import React from 'react'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

interface FormFieldProps {
  label: string
  name: string
  type?: 'text' | 'email' | 'password' | 'number' | 'select' | 'textarea' | 'checkbox'
  value: any
  onChange: (value: any) => void
  onBlur?: () => void
  error?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
  options?: Array<{ value: string; label: string }>
  step?: string
  rows?: number
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  required = false,
  disabled = false,
  className = '',
  options = [],
  step,
  rows = 3
}) => {
  const baseInputClasses = `w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-colors ${
    error
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (type === 'checkbox') {
      onChange((e.target as HTMLInputElement).checked)
    } else if (type === 'number') {
      onChange(e.target.value === '' ? '' : e.target.value)
    } else {
      onChange(e.target.value)
    }
  }

  const renderInput = () => {
    switch (type) {
      case 'select':
        return (
          <select
            id={name}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            className={baseInputClasses}
            required={required}
          >
            <option value="">Selecione...</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )

      case 'textarea':
        return (
          <textarea
            id={name}
            name={name}
            value={value || ''}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            placeholder={placeholder}
            rows={rows}
            className={baseInputClasses}
            required={required}
          />
        )

      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              id={name}
              name={name}
              type="checkbox"
              checked={value || false}
              onChange={handleChange}
              onBlur={onBlur}
              disabled={disabled}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              required={required}
            />
            <label htmlFor={name} className="ml-2 text-sm text-gray-700">
              {label}
            </label>
          </div>
        )

      default:
        return (
          <input
            id={name}
            name={name}
            type={type}
            value={value || ''}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            placeholder={placeholder}
            step={step}
            className={baseInputClasses}
            required={required}
          />
        )
    }
  }

  if (type === 'checkbox') {
    return (
      <div className={className}>
        {renderInput()}
        {error && (
          <div className="mt-1 flex items-center text-red-600 text-sm">
            <ExclamationCircleIcon className="w-4 h-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderInput()}
      {error && (
        <div className="mt-1 flex items-center text-red-600 text-sm">
          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
    </div>
  )
}

export default FormField